/**
 * SubNav — the secondary navigation row rendered below the primary header
 * when the Engagement group is active.
 *
 * Deliberate trip-wire crossing (ADR-034 / frontend/pro/CLAUDE.md §Navigation
 * shell): the Engagement group requires two levels of navigation (a primary
 * header entry + this sub-nav row). This is explicitly documented as the
 * intended design shape rather than a rail migration. See the CLAUDE.md
 * §Navigation shell note for the full rationale.
 *
 * ADR-034 styling: Tailwind-first, no static inline styles, tokens only.
 * All className values in this component are static string literals — no
 * glued interpolations — so no @layer components carve-out is needed beyond
 * what already exists in index.css (SILL-378 / ADR-034).
 */
import { NavLink } from "react-router-dom";
import type { SubNavTab } from "./nav";

export interface SubNavProps {
  tabs: SubNavTab[];
  /** Accessible label for the sub-nav landmark. */
  ariaLabel?: string;
}

export function SubNav({ tabs, ariaLabel = "Engagement navigation" }: SubNavProps) {
  return (
    <nav
      aria-label={ariaLabel}
      className="border-t border-white/10 bg-[linear-gradient(100deg,var(--pro-deep)_0%,var(--pro-deep2)_100%)]"
    >
      <div className="flex h-[38px] items-center gap-1 px-[22px]">
        {tabs.map((tab) => (
          <SubNavLink key={tab.id} tab={tab} />
        ))}
      </div>
    </nav>
  );
}

function SubNavLink({ tab }: { tab: SubNavTab }) {
  const prefixes = tab.matchPrefixes ?? [tab.to];
  return (
    <NavLink
      to={tab.to}
      end={false}
      className="relative flex h-full items-center px-3 text-[13px] font-medium no-underline outline-none transition-colors hover:text-white focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/30"
    >
      {({ isActive: routerActive }) => {
        // Derive active state from matchPrefixes so detail routes (e.g.
        // /campaigns/:id) also highlight the parent tab.
        const active =
          routerActive ||
          prefixes.some((p) => window.location.pathname.startsWith(`${p}/`));
        return (
          <>
            <span className={active ? "font-semibold text-white" : "text-white/65"}>
              {tab.label}
            </span>
            {active && (
              <span className="absolute inset-x-3 bottom-0 h-[2px] rounded-t-[2px] bg-pro-ledge" />
            )}
          </>
        );
      }}
    </NavLink>
  );
}
